/* eslint-disable no-tabs */
import React, { useState } from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  sortBy, filter, includes, first, isEmpty, last, get,
} from 'lodash';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { DateTime } from 'luxon';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Grid,
  Typography,
} from '@mui/material';
import {
  RunStatusNames, DataDriveRunTypes, isOnMobile, getDurationForDynamicRun,
  sortedRunTypes,
} from '../../../lib/utils';
import { EditRun } from './edit-run-modal-content';
import { CreateDynamicRun } from './create-dynamic-run-modal-content';
import { PendingRunModal } from './pending-run-modal';
import RadioSelect from './radio-select';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(localizedFormat);

const styles = {
  flexRow: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    alignContent: 'space-around',
  },
  button: {
    width: '140px',
    fontSize: '12px',
  },
  header: {
    fontSize: '24px',
  },
  content: {
    maxHeight: '480px',
    marginRight: '12px',
    paddingRight: '32px',
    maxWidth: '360px',
    overflowY: 'auto',
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
};

function BaseRunManagerModal({
  open,
  zones,
  wranglers,
  handleSave,
  handleClose,
  run,
  selectedMarket,
  tz,
  vehicleCounts,
  overallTarget,
  warehouses,
  allZones,
  getVehiclesAndLocations,
  deployableVehicles,
  flags,
  isDynamic,
  displayModalRadioSelector,
  editing,
  equityDeployMarkets,
}) {
  const [currentRun, setCurrentRun] = useState(run);
  const [assignees] = useState(undefined);
  const runDate = currentRun.startDate
    ? dayjs(currentRun.startDate.date).tz(tz).format('YYYY-MM-DD')
    : '';
  const [selectedDate, setSelectedDate] = useState(runDate);
  let runTime;
  if (currentRun.startDate) {
    if (currentRun.startDate.hasTime) {
      runTime = dayjs(currentRun.startDate.date).tz(tz).format('HH:mm');
    } else runTime = '';
  } else runTime = '';
  const [startTime, setStartTime] = useState(runTime);

  const getRunZones = (runRouteIds) => {
    const selectedZones = filter(allZones, (v) => includes(runRouteIds, v._id));
    return selectedZones;
  };

  const handleCurrentTarget = (runRouteIds) => {
    let target = 0;
    let runZones = [];
    runZones = getRunZones(runRouteIds);
    if (runZones && runZones.length > 0) {
      for (const runZone of runZones) {
        const count = overallTarget(runZone) - vehicleCounts(runZone);
        target += count;
      }
    }
    return target;
  };

  const runCurrentTarget = handleCurrentTarget(currentRun.routeIds);
  const [currentTarget, setCurrentTarget] = useState(runCurrentTarget);

  const sortedWranglers = sortBy(wranglers, (wrangler) => wrangler.firstName.toLowerCase());
  const assigneeOptions = sortedWranglers.map((wrangler) => ({
    value: wrangler._id,
    label: `${wrangler.firstName} ${wrangler.lastName}`,
    isDisabled: false,
  }));

  const statusOptions = run.status === RunStatusNames.IN_PROGRESS ? [{
    value: RunStatusNames.CLOSED,
    label: RunStatusNames.CLOSED,
  }] : [{
    value: RunStatusNames.TODO,
    label: RunStatusNames.TODO,
  }];

  const sortedZones = sortBy(zones, (zone) => zone.name.toLowerCase());
  const zoneOptions = sortedZones.map((zone) => ({
    value: zone._id,
    label: zone.name,
    locations: zone.locations,
    isDisabled: false,
  }));
  const notEmptyZones = zoneOptions.filter((zone) => zone.locations.length);
  const sortedWarehouses = sortBy(warehouses, (warehouse) => warehouse.name.toLowerCase());
  const warehouseOptions = sortedWarehouses.map((warehouse) => ({
    value: warehouse._id,
    label: warehouse.name,
    isDisabled: false,
  }));

  const dynamicRunFlagEnabled = !isEmpty(flags) && (flags[0] && flags[0].isActive);
  const routeTypes = ['Static', 'Dynamic'];
  const sortedRouteTypes = sortBy(routeTypes, (type) => type);
  const displayDynamicTab = dynamicRunFlagEnabled
    && currentRun.isDynamic !== false
    && !editing;
  const intialRouteType = displayDynamicTab ? first(sortedRouteTypes) : last(sortedRouteTypes);
  const [routeType, setRouteType] = useState(intialRouteType);
  const runTypes = [...sortedRunTypes];
  if (equityDeployMarkets && equityDeployMarkets.split(', ').includes(selectedMarket.name)) {
    runTypes.push({
      label: 'Equity Deployment', value: DataDriveRunTypes.EQUITY_DEPLOY,
    });
  }
  const runTypeOptions = sortBy(runTypes, (type) => type.label.toLowerCase());
  const showDeployQuantity = currentRun?.type !== DataDriveRunTypes.REBALANCE;
  const displayStatic = editing || (routeType && routeType === 'Static');

  const [openPendingRunModal, setOpenPendingRunModal] = useState(false);
  const [pendingRunAssignees, setPendingRunAssignees] = useState({});
  const marketVanCapacity = get(selectedMarket, 'marketVanCapacity', 50);

  const onSave = (updatedRun = {}) => {
    if (!isEmpty(updatedRun)) handleSave(run, updatedRun);
    else handleSave(run, currentRun);
  };

  const onSaveRun = () => {
    const tzString = DateTime.local().setZone(tz).offsetNameShort;
    if (selectedDate !== '' && startTime !== '') {
      const dateTime = new Date(`${selectedDate} ${startTime} ${tzString}`);
      const startDate = { date: dateTime, hasTime: true };
      currentRun.startDate = startDate;
    } else if (selectedDate !== '') {
      const dateTime = new Date(`${selectedDate} 12:00 ${tzString}`);
      const startDate = { date: dateTime, hasTime: false };
      currentRun.startDate = startDate;
    } else {
      delete currentRun.startDate;
    }

    setCurrentRun(currentRun);
    if (displayModalRadioSelector) {
      currentRun.isDynamic = routeType === 'Dynamic';
      if (currentRun.isDynamic === false) {
        delete currentRun.duration;
      }
    }
    if (currentRun.isDynamic && !editing) {
      setOpenPendingRunModal(!openPendingRunModal);
    } else {
      onSave();
    }
  };

  const handleChangeRun = (event) => {
    const newState = {
      ...currentRun,
      [event.target.name]: event.target.value,
    };

    setCurrentRun(newState);
  };

  const handleQuantityChange = (event) => {
    const newState = {
      ...currentRun,
      [event.target.name]: parseInt(event.target.value, 10),
    };

    setCurrentRun(newState);
  };

  const handleChangeRoute = (value = []) => {
    const newRoute = {
      ...currentRun,
      routeIds: value,
    };

    setCurrentRun(newRoute);
    const target = handleCurrentTarget(newRoute.routeIds);
    setCurrentTarget(target);
  };

  const handleChangeWarehouse = (value) => {
    const newWarehouse = {
      ...currentRun,
      warehouseId: value,
    };

    setCurrentRun(newWarehouse);
  };

  const handleChangeWrangler = (value) => {
    const newWrangler = {
      ...currentRun,
      wranglerIds: value,
    };

    setCurrentRun(newWrangler);
  };

  const handleChangeStatus = (value) => {
    const newStatus = {
      ...currentRun,
      status: value,
    };

    setCurrentRun(newStatus);
  };

  const handleChangeType = (value) => {
    const newType = {
      ...currentRun,
      type: value,
    };
    if ((currentRun?.type !== newType.type) || !currentRun?.duration) {
      newType.duration = getDurationForDynamicRun(newType, selectedMarket);
    }
    setCurrentRun(newType);
  };

  const handleCountChange = (event) => {
    const newState = {
      ...currentRun,
      [event.target.name]: parseFloat(event.target.value, 10),
    };
    setCurrentRun(newState);
  };

  const handleZonesChange = (value) => {
    const newState = {
      ...currentRun,
      zones: value,
    };
    setCurrentRun(newState);
  };

  const handleClosePendingRunModal = () => {
    setOpenPendingRunModal(!openPendingRunModal);
  };

  if (openPendingRunModal && !editing) {
    return (
      <PendingRunModal
        openPendingRunModal={openPendingRunModal}
        currentRun={currentRun}
        setCurrentRun={setCurrentRun}
        handleClose={handleClosePendingRunModal}
        handleSaveRun={onSave}
        assigneeOptions={assigneeOptions}
        pendingRunAssignees={pendingRunAssignees}
        setPendingRunAssignees={setPendingRunAssignees}
        marketVanCapacity={marketVanCapacity}
        styles={styles}
      />
    );
  }

  return (
    <Dialog maxWidth={isOnMobile() ? 'sm' : 'lg'} open={open} onClose={handleClose}>
      <DialogTitle style={styles.flexRow}>
        <Typography style={styles.header}>Run Builder</Typography>
      </DialogTitle>
      <DialogContent sx={{ pb: 5 }}>
        {dynamicRunFlagEnabled && displayModalRadioSelector
          && (
          <Grid>
            <Grid item container xs={12}>
              <RadioSelect
                value={routeType}
                valueArray={routeTypes}
                onClick={(e) => setRouteType(e.target.value)}
              />
            </Grid>
          </Grid>
          )}
        {displayStatic ? (
          <EditRun
            currentRun={currentRun}
            handleChangeRun={handleChangeRun}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            startTime={startTime}
            setStartTime={setStartTime}
            zoneOptions={zoneOptions}
            handleChangeRoute={handleChangeRoute}
            warehouseOptions={warehouseOptions}
            handleChangeWarehouse={handleChangeWarehouse}
            handleQuantityChange={handleQuantityChange}
            currentTarget={currentTarget}
            assignees={assignees}
            assigneeOptions={assigneeOptions}
            handleChangeWrangler={handleChangeWrangler}
            statusOptions={statusOptions}
            handleChangeStatus={handleChangeStatus}
            selectedMarket={selectedMarket}
            tz={tz}
            getVehiclesAndLocations={getVehiclesAndLocations}
            isDynamic={isDynamic}
          />
        ) : (
          <CreateDynamicRun
            currentRun={currentRun}
            runTypeOptions={runTypeOptions}
            handleChangeType={handleChangeType}
            showDeployQuantity={showDeployQuantity}
            handleCountChange={handleCountChange}
            deployableVehicles={deployableVehicles}
            zoneOptions={notEmptyZones}
            handleZonesChange={handleZonesChange}
            styles={styles}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color="primary"
          variant="outlined"
          style={styles.button}
        >
          Cancel
        </Button>
        <Button
          onClick={onSaveRun}
          color="primary"
          variant="contained"
          style={styles.button}
        >
          {displayStatic ? 'Save and Close' : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

BaseRunManagerModal.defaultProps = {};
BaseRunManagerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleSave: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  overallTarget: PropTypes.func.isRequired,
  vehicleCounts: PropTypes.func.isRequired,
  getVehiclesAndLocations: PropTypes.func.isRequired,
  run: PropTypes.shape({
    _id: PropTypes.string,
  }).isRequired,
  wranglers: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
    }).isRequired,
  ).isRequired,
  zones: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
    }).isRequired,
  ).isRequired,
  allZones: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
    }).isRequired,
  ).isRequired,
  warehouses: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
    }).isRequired,
  ).isRequired,
  selectedMarket: PropTypes.shape({
    _id: PropTypes.string,
  }).isRequired,
  deployableVehicles: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
    }).isRequired,
  ).isRequired,
  flags: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
    }).isRequired,
  ).isRequired,
  isDynamic: PropTypes.bool.isRequired,
  displayModalRadioSelector: PropTypes.bool.isRequired,
  editing: PropTypes.bool.isRequired,
};

const RunManagerModal = withRouter(BaseRunManagerModal);
export { BaseRunManagerModal, RunManagerModal };
